/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { Fragment, h, JSX } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import type { Language, Players } from '../../../types/player-api-types.ts';
import { usePlayerData } from '../hooks/usePlayerData.tsx';
import { CONTROL_BAR_HEIGHT, DEFAULT_ASPECT } from '../utilities/constants.ts';
import { BigPlayButton } from '../../media/players/vulcanV2Player/shared/ui_components/BigPlayButton.tsx';
import { ProgressiveThumbnail } from '../../shared/ProgressiveThumbnail.jsx';
import { getLanguage } from '../../shared/translations.js';
import { thumbnailAssets as getThumbnailAssets } from '../../../utilities/assets.js';
import { controlMultiplierEstimatedByWidth } from '../../../utilities/fit-control.js';
import {
  getDefaultBigPlayButtonBorderRadius,
  getDefaultControlBarDistance,
  getDefaultPlayerBorderRadius,
} from '../../../utilities/roundedPlayerDefaults.ts';
import { getCssGradient, getGradientColor } from '../../../utilities/gradients.ts';
import { isGradient } from '../../../types/gradient.ts';

const MAX_SCALE = 1.3;
const MIN_SCALE = 0.3;
const DEFAULT_LOWER_CUTOFF_WIDTH = 640;
const DEFAULT_UPPER_CUTOFF_WIDTH = 960;

interface PreloadThumbnailProps {
  isPlayPending: boolean;
  mediaId: string;
  playerType: Players;
  playerWidth: number;
}

export const PreloadThumbnail = ({
  isPlayPending,
  mediaId,
  playerType,
  playerWidth,
}: PreloadThumbnailProps): JSX.Element => {
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isPlayPending) {
      setIsLoading(true);
    }
  }, [isPlayPending]);

  const { embedOptions, mediaData } = usePlayerData();
  const {
    aspect,
    bigPlayButton,
    bpbTime,
    bigPlayButtonBorderRadius,
    controlBarBorderRadius,
    controlsVisibleOnLoad,
    floatingControlBar,
    noMixBlendMode,
    playButton,
    playerBorderRadius,
    playerColor,
    playerColorGradient,
    playerLanguage,
    roundedPlayer,
    thumbnailAltText,
    transparentLetterbox,
  } = embedOptions;
  const { aspectRatio, assets, duration, mediaType, name } = mediaData;
  const height = playerWidth / (aspect ?? aspectRatio ?? DEFAULT_ASPECT);

  const scale = Math.min(
    MAX_SCALE,
    Math.max(
      MIN_SCALE,
      controlMultiplierEstimatedByWidth(playerWidth, [
        DEFAULT_LOWER_CUTOFF_WIDTH,
        DEFAULT_UPPER_CUTOFF_WIDTH,
      ]),
    ),
  );

  const calculatedControlsVisibleOnLoad = controlsVisibleOnLoad && playerType === 'vulcan-v2';

  const defaultControlBarDistance = getDefaultControlBarDistance({
    controlBarBorderRadius,
    floatingControlBar,
    roundedPlayer,
  });
  const calculatedControlBarDistance = calculatedControlsVisibleOnLoad
    ? (CONTROL_BAR_HEIGHT / 2 + defaultControlBarDistance) * scale
    : defaultControlBarDistance * scale;

  const calculatedBigPlayButtonBorderRadius =
    getDefaultBigPlayButtonBorderRadius({
      bigPlayButtonBorderRadius,
      roundedPlayer,
    }) * scale;

  const calculatedPlayerBorderRadius =
    getDefaultPlayerBorderRadius({
      playerBorderRadius,
      roundedPlayer,
    }) * scale;

  const backgroundColor =
    transparentLetterbox === true || calculatedPlayerBorderRadius > 0 ? 'transparent' : undefined;

  const thumbnailAssets = getThumbnailAssets(assets, {});

  const calculatedPlayerLanguage =
    (getLanguage(playerLanguage) as Language | undefined) ?? (getLanguage('en-US') as Language);

  const shouldShowBigPlayButton = bigPlayButton !== false && playButton !== false;
  const shouldShowBpbTime = bpbTime === true || bpbTime === 'true';

  const backgroundGradientCss = playerColorGradient?.on
    ? getCssGradient(playerColorGradient)
    : undefined;

  // if the gradient is enabled and valid, use the first color in the gradient as the player color,
  // otherwise, just use the player color like normal
  const playerColorOrGradientColor =
    playerColorGradient?.on && isGradient(playerColorGradient)
      ? (getGradientColor(playerColorGradient) ?? playerColor)
      : playerColor;

  const handleClick = () => {
    setIsLoading(true);
  };

  return (
    <Fragment>
      <div
        class="w-css-reset"
        style={{
          cursor: 'pointer',
          width: '100%',
          height: `${height}px`,
        }}
        onClick={handleClick}
        tabIndex={shouldShowBigPlayButton ? -1 : 0}
      >
        <ProgressiveThumbnail
          backgroundColor={backgroundColor}
          images={thumbnailAssets}
          isVisible={true}
          hashedId={mediaId}
          playerBorderRadius={calculatedPlayerBorderRadius}
          swatchEnabled={false}
          uiHasRendered={false}
          thumbnailAltText={thumbnailAltText ?? ''}
          height={`${height}px`}
        />
      </div>
      {shouldShowBigPlayButton && (
        <BigPlayButton
          borderRadius={calculatedBigPlayButtonBorderRadius}
          buttonTabIndex={0}
          color={playerColorOrGradientColor}
          controlBarDistance={calculatedControlBarDistance}
          duration={duration ?? 0}
          isLiveMedia={mediaType === 'LiveStream'}
          isLoading={isLoading}
          isVisible={true}
          noMixBlendMode={noMixBlendMode}
          onClick={handleClick}
          playerLanguage={calculatedPlayerLanguage}
          scale={scale}
          showBpbTime={shouldShowBpbTime}
          videoName={name ?? ''}
          videoWidth={playerWidth}
          backgroundGradientCss={backgroundGradientCss}
        ></BigPlayButton>
      )}
    </Fragment>
  );
};
